/**
 * FadeScroll — a vertical scroll container with NO visible scrollbar and a subtle
 * fade-to-background gradient at the bottom edge when more content exists below
 * (Quick 260616-ggl #1/#2). The fade disappears once scrolled to the bottom (or
 * when the content fits). Scrolling still works (wheel / trackpad / touch).
 *
 * Pass `fadeColor` matching the container background so the gradient reads as a
 * fade-out rather than a colored band.
 */

import * as React from 'react';

export interface FadeScrollProps {
  readonly children: React.ReactNode;
  /** Bottom gradient target — match the container background. */
  readonly fadeColor?: string;
  /** Applied to the outer wrapper (e.g. flex sizing). */
  readonly style?: React.CSSProperties;
  readonly className?: string;
}

export function FadeScroll({
  children,
  fadeColor = 'var(--color-bg-secondary)',
  style,
  className,
}: FadeScrollProps): React.JSX.Element {
  const ref = React.useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = React.useState(false);

  const update = React.useCallback((): void => {
    const el = ref.current;
    if (el === null) return;
    const overflowing = el.scrollHeight - el.clientHeight > 1;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setShowFade(overflowing && !atBottom);
  }, []);

  React.useEffect(() => {
    update();
    const el = ref.current;
    // Guard: jsdom (tests) has no ResizeObserver. onScroll + the children dep still
    // keep the fade in sync there; real browsers get live resize tracking.
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update, children]);

  return (
    <div style={{ position: 'relative', minHeight: 0, ...style }} className={className}>
      <div
        ref={ref}
        onScroll={update}
        className="no-scrollbar"
        style={{ height: '100%', overflowY: 'auto' }}
      >
        {children}
      </div>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '32px',
          pointerEvents: 'none',
          background: `linear-gradient(to bottom, transparent, ${fadeColor})`,
          opacity: showFade ? 1 : 0,
          transition: 'opacity 120ms ease',
        }}
      />
    </div>
  );
}
